import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { Fragment } from 'react';

import { PageHead } from '_components/base/head';
import { PublicGoogleTagManager } from '_components/common';
import { Public404Page } from '_components/error/public-404-page';
import { LayoutPagesRouter } from '_components/layout/LayoutPagesRouter';
import { PageSeoTag } from '_components/SEO';
import { extractDomainRelevantInfoFromHeaders } from '_config/domain.config';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { isSerializedError, SerializedError, serializeError } from '_core/error/error-serializer';
import { PublicApiRepo } from '_core/repos/public-api.repo';
import { baseSsrPrefetchQueries } from '_core/utils/public-api';
import { getServerSideTranslations } from '_core/utils/ssr';
import { AgencyClaimLandingPage } from '_features/agency/claim/agency-claim-landing.page';
import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';

const DOMAIN_CONFIG_ERROR_STATUSES = [400, 404, 410];

type Props = {
  locale: string;
  canonical: string;
  origin: string;
  agencySlug: string | null;
  error: SerializedError | null;
  publicApiNavBarUrl?: string;
  dehydratedState?: DehydratedState;
};

export default function PageRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);

  const { canonical, origin, error, locale = 'en', agencySlug, publicApiNavBarUrl = '' } = props;

  if (error && isSerializedError(error)) {
    const { status } = error;
    if (DOMAIN_CONFIG_ERROR_STATUSES.includes(status as number))
      return <Public404Page message={t('common:pageErrors.notFound.agencyNotFound')} locale={locale} />;
  }

  PublicApiRepo.publicApiNavBarUrl = publicApiNavBarUrl;

  return (
    <Fragment>
      <PageHead />
      <PublicAppContextProvider
        canonical={canonical}
        origin={origin}
        locale={locale}
        briefingOptions={{
          page: 'claim-agency',
        }}>
        <PublicGoogleTagManager />
        <PageSeoTag
          title={t('agency:claim.seo.title')}
          description={t('agency:claim.seo.description')}
          canonical={canonical}
        />
        <LayoutPagesRouter className="bg-secondary-50" showFooter={false}>
          <AgencyClaimLandingPage />
        </LayoutPagesRouter>
      </PublicAppContextProvider>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const agencySlug = context.query?.agencySlug?.[0] ?? null;
  let error: SerializedError | null = null;
  let domainRelevantInfo;
  const { i18nNamespaces } = agencyClaimConfig;

  try {
    domainRelevantInfo = extractDomainRelevantInfoFromHeaders(context.req);
  } catch (e) {
    // @TODO: use a more elegant way when upgrade to NextJS 10 https://nextjs.org/blog/next-10#redirect-and-notfound-support-for-getstaticprops--getserversideprops
    context.res.statusCode = (e as any).status;
    error = serializeError(e as Error);

    return {
      props: {
        locale: 'en',
        canonical: '',
        origin: '',
        error,
        agencySlug,
        ...(await getServerSideTranslations('en', i18nNamespaces)),
      },
    };
  }

  const { canonical, origin, locale } = domainRelevantInfo;

  const queryClient = new QueryClient();
  let publicApiNavBarUrl = '';

  try {
    const response = await baseSsrPrefetchQueries(origin, locale, context.resolvedUrl, queryClient);
    publicApiNavBarUrl = response.publicApiNavBarUrl;
  } catch (e) {
    // Limitation: Typescript exceptions are not typed, so we rely on what is supposed to happen
    if ((e as any).status !== 404) {
      throw e;
    }
  }

  return {
    props: {
      locale,
      canonical,
      origin,
      agencySlug,
      error,
      publicApiNavBarUrl,
      dehydratedState: dehydrate(queryClient),
      ...(await getServerSideTranslations(locale, i18nNamespaces)),
    },
  };
};
